
import React, { createContext, useContext, useState, useEffect } from 'react';

interface TimezoneContextType {
  timezone: string;
  setTimezone: (timezone: string) => void;
  userTimezone: string;
  isTimezoneReady: boolean;
  formatInUserTimezone: (dateString: string, options?: Intl.DateTimeFormatOptions) => string;
  toUTC: (localDateString: string) => string;
  fromUTC: (utcDateString: string) => string;
  getCurrentDateInUserTimezone: () => string;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

export const TimezoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [timezone, setTimezone] = useState(() => {
    // Get user's local timezone
    const saved = localStorage.getItem('user-timezone');
    return saved || Intl.DateTimeFormat().resolvedOptions().timeZone;
  });

  const [isTimezoneReady, setIsTimezoneReady] = useState(true);

  useEffect(() => {
    // Save timezone preference to localStorage
    localStorage.setItem('user-timezone', timezone);
  }, [timezone]);

  const formatInUserTimezone = (dateString: string, options?: Intl.DateTimeFormatOptions): string => {
    if (!dateString) return dateString;
    
    try {
      const date = new Date(dateString + 'T12:00:00Z'); // Use noon UTC to avoid DST issues
      const defaultOptions: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: timezone
      };
      
      return new Intl.DateTimeFormat('pt-BR', { ...defaultOptions, ...options }).format(date);
    } catch (error) {
      console.error('Error formatting date in timezone:', error);
      return dateString;
    }
  };

  const toUTC = (localDateString: string): string => {
    if (!localDateString) return localDateString;
    
    try {
      console.log('Converting to UTC - Input:', localDateString, 'Timezone:', timezone);
      
      // Parse the date components
      const [year, month, day] = localDateString.split('-').map(Number);
      
      // Create a date in the user's timezone at noon to avoid DST issues
      const localDate = new Date();
      localDate.setFullYear(year, month - 1, day);
      localDate.setHours(12, 0, 0, 0);
      
      // Use Intl.DateTimeFormat to get the date parts in the user's timezone
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      
      // Get the actual date in the user's timezone
      const parts = formatter.formatToParts(localDate);
      const userYear = parts.find(p => p.type === 'year')?.value;
      const userMonth = parts.find(p => p.type === 'month')?.value;
      const userDay = parts.find(p => p.type === 'day')?.value;
      
      // If the formatted date matches our input, the local date is correct
      const userDateString = `${userYear}-${userMonth}-${userDay}`;
      
      if (userDateString === localDateString) {
        // Convert to UTC
        const utcDate = new Date(localDate.toLocaleString('en-US', { timeZone: 'UTC' }));
        const utcString = utcDate.toISOString().split('T')[0];
        console.log('Converted to UTC:', utcString);
        return utcString;
      } else {
        // If dates don't match, adjust
        const targetDate = new Date(`${localDateString}T12:00:00`);
        const utcString = targetDate.toISOString().split('T')[0];
        console.log('Direct conversion to UTC:', utcString);
        return utcString;
      }
    } catch (error) {
      console.error('Error converting to UTC:', error);
      return localDateString;
    }
  };

  const fromUTC = (utcDateString: string): string => {
    if (!utcDateString) return utcDateString;
    
    try {
      console.log('Converting from UTC - Input:', utcDateString, 'Timezone:', timezone);
      
      // Create UTC date at noon to avoid DST issues
      const utcDate = new Date(utcDateString + 'T12:00:00Z');
      
      // Format the date in the user's timezone
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      
      const parts = formatter.formatToParts(utcDate);
      const year = parts.find(p => p.type === 'year')?.value;
      const month = parts.find(p => p.type === 'month')?.value;
      const day = parts.find(p => p.type === 'day')?.value;
      
      const localDateString = `${year}-${month}-${day}`;
      console.log('Converted from UTC:', localDateString);
      return localDateString;
    } catch (error) {
      console.error('Error converting from UTC:', error);
      return utcDateString;
    }
  };

  const getCurrentDateInUserTimezone = (): string => {
    try {
      console.log('Getting current date in timezone:', timezone);
      
      const now = new Date();
      
      // Use Intl.DateTimeFormat to get current date in user's timezone
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      
      const parts = formatter.formatToParts(now);
      const year = parts.find(p => p.type === 'year')?.value;
      const month = parts.find(p => p.type === 'month')?.value;
      const day = parts.find(p => p.type === 'day')?.value;
      
      const dateString = `${year}-${month}-${day}`;
      console.log('Current date in user timezone:', dateString);
      return dateString;
    } catch (error) {
      console.error('Error getting current date in timezone:', error);
      return new Date().toISOString().split('T')[0];
    }
  };

  return (
    <TimezoneContext.Provider value={{ 
      timezone, 
      setTimezone,
      userTimezone: timezone,
      isTimezoneReady,
      formatInUserTimezone,
      toUTC,
      fromUTC,
      getCurrentDateInUserTimezone
    }}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (context === undefined) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};
